import { apiClient } from '@/shared/api/client';

export interface FeedRecord {
  id: string;
  pondId: string;
  pondName?: string;
  feedTime: string;
  /** Brand as sent by the API (`brand`); kept so raw responses still type-check */
  brand?: string;
  /** Pellet size / feed type as sent by the API (`feedSize`) */
  feedSize?: string;
  feedBrand: string;
  feedType: string;
  totalFeed: number;
  appetite: number;
  maximumFeed: number;
  /** total/maximum as a percentage; derived client-side when the API omits it */
  feedEfficiency: number;
  feedConversionRatio?: number | null;
  notes?: string;
  latitude?: number | null;
  longitude?: number | null;
  performedByName?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface FeedSummary {
  totalFeedUsed: number;
  averageAppetite: number;
  averageFeedEfficiency: number;
  totalRecords: number;
  pondBreakdown: Array<{
    pondId: string;
    pondName: string;
    totalFeed: number;
    averageAppetite: number;
  }>;
}

export interface FeedAnalytics {
  feedByBrand: Record<string, number>;
  feedByType: Record<string, number>;
  byBrand?: Record<string, number>;
  byType?: Record<string, number>;
  appetiteTrend: Array<{ date: string; appetite: number }>;
  efficiencyTrend: Array<{ date: string; efficiency: number }>;
}

export interface TrendDataPoint {
  date: string;
  value: number;
}

export interface FeedStatistics {
  totalRecords: number;
  recordsThisWeek: number;
  recordsThisMonth: number;
  avgAppetite: number;
  avgFeedEfficiency: number;
  totalFeedUsed: number;
  lowAppetiteCount: number;
}

export interface FeedAlert {
  pondId: string;
  pondName: string;
  alertType: string;
  alertMessage: string;
  severity: 'info' | 'warning' | 'critical';
  lastFeeding: string | null;
}

export interface FeedRecordCreateRequest {
  feedType: string
  feedBrand: string
  totalFeed: number
  maximumFeed: number
  appetite: number
  feedEfficiency: number
  feedTime: string
  notes?: string
  imageIds?: string[]
}

export type FeedRecordUpdateRequest = Partial<FeedRecordCreateRequest>

/**
 * The API returns `brand` / `feedSize` and no efficiency figure; the UI reads
 * `feedBrand` / `feedType` / `feedEfficiency`. Normalise once at the boundary.
 */
const normalizeFeedRecord = (raw: FeedRecord): FeedRecord => {
  const maximumFeed = Number(raw.maximumFeed) || 0;
  const totalFeed = Number(raw.totalFeed) || 0;
  return {
    ...raw,
    feedBrand: raw.feedBrand ?? raw.brand ?? '',
    feedType: raw.feedType ?? raw.feedSize ?? '',
    feedEfficiency:
      raw.feedEfficiency ?? (maximumFeed > 0 ? Math.min((totalFeed / maximumFeed) * 100, 100) : 0),
  };
};

export const feedApi = {
  // Create a new feed record
  create: async (
    farmId: string,
    pondId: string,
    data: FeedRecordCreateRequest
  ): Promise<FeedRecord> => {
    const response = await apiClient.post<{ data: FeedRecord }>(
      `/farms/${farmId}/ponds/${pondId}/feed-records`,
      data
    )
    return response.data.data
  },

  // Update an existing feed record
  update: async (
    farmId: string,
    pondId: string,
    recordId: string,
    data: FeedRecordUpdateRequest
  ): Promise<FeedRecord> => {
    const response = await apiClient.put<{ data: FeedRecord }>(
      `/farms/${farmId}/ponds/${pondId}/feed-records/${recordId}`,
      data
    )
    return response.data.data
  },

  // List feed records for a farm
  listRecords: async (
    farmId: string,
    params?: {
      cursor?: string;
      limit?: number;
      pondId?: string;
      startDate?: string;
      endDate?: string;
      filter?: 'low_appetite';
    }
  ) => {
    const response = await apiClient.get<{
      data: {
        records: FeedRecord[];
        pagination: { nextCursor?: string; hasNext: boolean };
      };
    }>(`/farms/${farmId}/feed`, { params });
    return { data: (response.data.data.records || []).map(normalizeFeedRecord), pagination: response.data.data.pagination };
  },

  // Get feed records for a specific pond
  getPondRecords: async (
    farmId: string,
    pondId: string,
    params?: { cursor?: string; limit?: number }
  ) => {
    const response = await apiClient.get<{
      data: FeedRecord[];
      pagination: { nextCursor?: string; hasNext: boolean };
    }>(`/farms/${farmId}/ponds/${pondId}/feed-records`, { params });
    return { ...response.data, data: (response.data.data || []).map(normalizeFeedRecord) };
  },

  // Get a specific feed record
  getRecord: async (farmId: string, pondId: string, recordId: string) => {
    const response = await apiClient.get<{ data: FeedRecord }>(
      `/farms/${farmId}/ponds/${pondId}/feed-records/${recordId}`
    );
    return normalizeFeedRecord(response.data.data);
  },

  // Get feed summary for a farm
  getSummary: async (
    farmId: string,
    params?: { startDate?: string; endDate?: string }
  ) => {
    const response = await apiClient.get<FeedSummary>(
      `/farms/${farmId}/feed/summary`,
      { params }
    );
    return response.data;
  },

  // Get feed analytics
  getAnalytics: async (
    farmId: string,
    params?: { startDate?: string; endDate?: string }
  ) => {
    const response = await apiClient.get<FeedAnalytics>(
      `/farms/${farmId}/feed/analytics`,
      { params }
    );
    return response.data;
  },

  // Get trend data for a specific parameter
  getTrends: async (
    farmId: string,
    params: {
      parameter: 'appetite' | 'total_feed' | 'feed_efficiency';
      days: number;
      pondId?: string;
    }
  ) => {
    const response = await apiClient.get<{ data: TrendDataPoint[] }>(
      `/farms/${farmId}/feed/trends`,
      { params }
    );
    return response.data;
  },

  // Get statistics for analytics (derived from summary endpoint)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getStatistics: async (farmId: string, _params?: { pondIds?: string[]; days?: number }): Promise<FeedStatistics> => {
    try {
      const response = await apiClient.get<FeedSummary>(
        `/farms/${farmId}/feed/summary`
      );
      const summary = response.data;
      // Map summary to statistics format
      return {
        totalRecords: summary.totalRecords || 0,
        recordsThisWeek: 0, // Not available from summary
        recordsThisMonth: 0, // Not available from summary
        avgAppetite: summary.averageAppetite || 0,
        avgFeedEfficiency: summary.averageFeedEfficiency || 0,
        totalFeedUsed: summary.totalFeedUsed || 0,
        lowAppetiteCount: 0, // Not available from summary
      };
    } catch {
      // Return empty statistics on error
      return {
        totalRecords: 0,
        recordsThisWeek: 0,
        recordsThisMonth: 0,
        avgAppetite: 0,
        avgFeedEfficiency: 0,
        totalFeedUsed: 0,
        lowAppetiteCount: 0,
      };
    }
  },

  // Get feed alerts
  getAlerts: async (farmId: string, params?: { severity?: string; type?: string }) => {
    const response = await apiClient.get<{
      data: FeedAlert[];
    }>(`/farms/${farmId}/feed/alerts`, { params });
    return response.data.data || [];
  },

  // Get available feed brands
  getBrands: async () => {
    const response = await apiClient.get<string[]>('/feed/brands');
    return response.data;
  },

  // Export records as CSV
  exportRecords: async (
    farmId: string,
    params?: { pondId?: string; startDate?: string; endDate?: string }
  ) => {
    const response = await apiClient.get(`/farms/${farmId}/feed/export`, {
      params,
      responseType: 'blob',
    });
    return response.data;
  },
};
